<template>
    <BaseDialog
        v-bind="{ ...$attrs, ...(cancelDisabled ? { 'disable-close-on-backdrop-click': true } : {}) }"
        v-model:open="open"
        :title="title"
        :description="description"
        :disable-portal="disablePortal"
        :as-child="asChild"
        :show-close="!cancelDisabled"
        @close="onCancelClicked">
        <div class="flex min-w-0 max-w-full flex-col gap-4" :class="containerClass">
            <slot name="icon"></slot>
            <div v-if="text" class="body text-ink-muted select-all" data-testid="dialog-text">{{ text }}</div>
            <slot name="content"></slot>
        </div>
        <template #footer>
            <div v-if="actionsJustify === 'between'" class="flex w-full items-center justify-between gap-2" data-testid="dialog-actions">
                <BaseButton theme="neutral" :disabled="cancelDisabled" data-testid="cancel-button" @click="onCancelClicked">{{ cancelLabel ?? t("common.cancel") }}</BaseButton>
                <BaseButton :theme="confirmTheme" :disabled="confirmDisabled" data-testid="confirm-button" @click="onConfirmClicked">
                    {{ confirmLabel ?? t("common.ok") }}
                </BaseButton>
            </div>
            <template v-else>
                <BaseButton theme="neutral" :disabled="cancelDisabled" data-testid="cancel-button" @click="onCancelClicked">{{ cancelLabel ?? t("common.cancel") }}</BaseButton>
                <BaseButton :theme="confirmTheme" :disabled="confirmDisabled" data-testid="confirm-button" @click="onConfirmClicked">
                    {{ confirmLabel ?? t("common.ok") }}
                </BaseButton>
            </template>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useI18n } from "vue-i18n";

import type { ButtonTheme } from "./BaseButton.vue";
import BaseButton from "./BaseButton.vue";
import BaseDialog from "./BaseDialog.vue";

defineOptions({ inheritAttrs: false });

const { t } = useI18n();

type ConfirmationDialogProps = {
    title?: string;
    text?: string;
    description?: string;
    confirmLabel?: string;
    cancelLabel?: string;
    actionsJustify?: "end" | "between";
    // Theme for the confirm button; use "danger" for destructive confirmations.
    confirmTheme?: ButtonTheme;
    disablePortal?: boolean;
    asChild?: boolean;
    // Disables the confirm button (e.g. while a required input is invalid or a request is in flight).
    confirmDisabled?: boolean;
    // Prevents Cancel, Close, Escape and backdrop dismissal while async work is pending.
    cancelDisabled?: boolean;
    // When false, the dialog stays open after confirm so the parent can run async work and close it itself.
    closeOnConfirm?: boolean;
    // uses props over :deep() selectors since this element is portaled to body
    containerClass?: string | Record<string, unknown> | unknown[];
};

const {
    title = "",
    text = "",
    description = "",
    confirmLabel,
    cancelLabel,
    actionsJustify = "end",
    confirmTheme = "primary",
    disablePortal = false,
    asChild = true,
    confirmDisabled = false,
    cancelDisabled = false,
    closeOnConfirm = true,
    containerClass,
} = defineProps<ConfirmationDialogProps>();

const emit = defineEmits<{
    confirm: [];
    cancel: [];
}>();

const open = defineModel<boolean>("open", {
    type: Boolean,
    default: false,
});

const onConfirmClicked = () => {
    if (confirmDisabled) {
        return;
    }
    if (closeOnConfirm) {
        open.value = false;
    }
    emit("confirm");
};

const onCancelClicked = () => {
    if (cancelDisabled) {
        return;
    }
    open.value = false;
    emit("cancel");
};
</script>
